import React, { useState, useEffect } from "react";
import { ref, get, child } from "firebase/database";
import { database } from "../firebase";
import { useNavigate, Link } from "react-router-dom";
import logo from "./Assets/logo.png";
import styles from "./login.module.css";
import ReCAPTCHA from "react-google-recaptcha";

const Login = () => {
  const [loginType, setLoginType] = useState("user"); // Default to user login
  const [emailOrName, setEmailOrName] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [termsContent, setTermsContent] = useState("");
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isTermsModalOpen && !termsContent) {
      fetch("/terms.txt")
        .then((response) => response.text())
        .then((text) => setTermsContent(text))
        .catch((error) => {
          console.error("Error fetching terms.txt:", error);
          setTermsContent("Unable to load Terms and Conditions.");
        });
    }
  }, [isTermsModalOpen, termsContent]);

  const handleLoginTypeToggle = () => {
    setLoginType(loginType === "user" ? "admin" : "user");
    setEmailOrName("");
    setPassword("");
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!captchaValue) {
      setMessage("Please complete the CAPTCHA.");
      return;
    }

    try {
      const dbRef = ref(database);

      if (loginType === "admin") {
        const snapshot = await get(child(dbRef, `admins`));
        if (snapshot.exists()) {
          const admins = snapshot.val();
          const adminKeys = Object.keys(admins);
          let found = false;
          let adminName = "";

          for (let key of adminKeys) {
            if (
              admins[key].name === emailOrName.trim() &&
              admins[key].password === password
            ) {
              found = true;
              adminName = admins[key].name;
              break;
            }
          }
          if (found) {
            localStorage.setItem(
              "currentUser",
              JSON.stringify({ type: "admin", name: adminName })
            );
            navigate("/admin");
          } else {
            setMessage("Invalid Admin Credentials.");
          }
        } else {
          setMessage("No admin data available.");
        }
      } else {
        // User login
        const snapshot = await get(child(dbRef, `users`));
        if (snapshot.exists()) {
          const users = snapshot.val();
          const userKeys = Object.keys(users);
          let found = false;
          let userEmail = "";

          for (let key of userKeys) {
            if (
              users[key].email.toLowerCase() === emailOrName.trim().toLowerCase() &&
              users[key].password === password
            ) {
              found = true;
              userEmail = users[key].email;
              break;
            }
          }
          if (found) {
            localStorage.setItem(
              "currentUser",
              JSON.stringify({ type: "user", email: userEmail })
            );
            navigate("/user");
          } else {
            setMessage("Invalid User Credentials.");
          }
        } else {
          setMessage("No user data available.");
        }
      }
    } catch (error) {
      console.error("Error during login:", error);
      setMessage("An error occurred during login. Please try again.");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.overlay}></div>

      {isTermsModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button
              className={styles.modalCloseButton}
              onClick={() => {
                setIsTermsModalOpen(false);
                setHasAgreed(false);
              }}
            >
              &times;
            </button>
            <h2 className={styles.modalHeader}>Terms and Conditions</h2>
            <div className={styles.modalText}>
              {termsContent || "Loading Terms and Conditions..."}
            </div>
            <div className={styles.modalButtonContainer}>
              <button
                className={styles.modalButton}
                style={{ backgroundColor: "#28a745" }}
                onClick={() => {
                  setHasAgreed(true);
                  setIsTermsModalOpen(false);
                }}
              >
                Accept
              </button>
              <button
                className={styles.modalButton}
                style={{ backgroundColor: "#dc3545" }}
                onClick={() => {
                  setHasAgreed(false);
                  setIsTermsModalOpen(false);
                }}
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.card}>
        <img src={logo} alt="App Logo" className={styles.logo} />
        
        <div className={styles.loginTypeToggle}>
          <button
            className={`${styles.toggleButton} ${loginType === "user" ? styles.active : ""}`}
            onClick={() => setLoginType("user")}
          >
            User Login
          </button>
          <button
            className={`${styles.toggleButton} ${loginType === "admin" ? styles.active : ""}`}
            onClick={() => setLoginType("admin")}
          >
            Admin Login
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputContainer}>
            <input
              type={loginType === "user" ? "email" : "text"}
              placeholder={loginType === "user" ? "Email" : "Name"}
              value={emailOrName}
              onChange={(e) => setEmailOrName(e.target.value)}
              required
              className={styles.input}
            />
          </div>
          <div className={styles.inputContainer}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={styles.input}
            />
          </div>

          {loginType === "user" && (
            <>
              <div className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id="agreeTerms"
                  checked={hasAgreed}
                  onChange={(e) => setHasAgreed(e.target.checked)}
                  className={styles.checkbox}
                />
                <label htmlFor="agreeTerms" className={styles.termsLabel}>
                  I agree to{" "}
                  <span
                    className={styles.termsLink}
                    onClick={() => setIsTermsModalOpen(true)}
                  >
                    Terms and Conditions
                  </span>
                </label>
              </div>

              <Link to="/forgot" className={styles.forgotPassword}>
                Forgot Password?
              </Link>
            </>
          )}

          <div className={styles.recaptchaContainer}>
            <ReCAPTCHA
              sitekey="6Ld009EqAAAAAPP-rFxbwELG8SvGV6Xvj3KNR1Vs"
              onChange={(value) => setCaptchaValue(value)}
              size={window.innerWidth < 400 ? "compact" : "normal"}
            />
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={
              (loginType === "user" && (!hasAgreed || !captchaValue)) ||
              (loginType === "admin" && !captchaValue)
            }
          >
            Login as {loginType === "user" ? "User" : "Admin"}
          </button>

          <button
          type="button"
          className={styles.submitCreateButton}
          onClick={() => window.location.href = "/create"}
        >
          Create Account
        </button>

        </form>

        {message && (
          <div
            className={styles.message}
            style={{
              backgroundColor: message.includes("Invalid")
                ? "#f8d7da"
                : "#d4edda",
              color: message.includes("Invalid") ? "#721c24" : "#155724",
            }}
          >
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;